/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { string, shape, number } from 'prop-types';
import loadable from '@loadable/component';
import mbpLogger from 'mbp-logger';

import { useSelector, useDispatch } from 'react-redux';
import useSalesforceProductRecommendationQuery from '../../../../helpers/Personalization/useSalesforceProductRecommendationQuery';
import useClickStreamCustomEventsTracking from '../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import ComponentErrorBoundary from '../../EnterpriseID/Common/ComponentErrorBoundary/ComponentErrorBoundary';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import TrendingProductRecsBuilder from './TrendingProductRecsBuilder';
import { getPersonalizedReminder, getSalesforceAttributes } from '../../../../../state/ducks/App/App-Selectors';
import CarouselBuilderSkeleton from '../../GraphqlSkeletonComponents/CarouselBuilderSkeleton';
import TabTrendingProductSkeleton from '../../GraphqlSkeletonComponents/TabTrendingProductSkeleton';

const LoadableSimpleCarouselBuilder = loadable(() => import(/* webpackChunkName: "SimpleCarouselBuilder" */ '../../EnterpriseID/Common/SimpleCarouselBuilder'));

const TrendingProductsABContainer = ({
    blockData,
}) => {
    const dispatch = useDispatch();
    const personalizedReminders = useSelector(getPersonalizedReminder);

    const {
        title: cmsTitle,
        css_classes,
        title_font_color,
        title_font_family,
        title_font_size,
        title_font_style,
        title_margin,
        align_title,
        products_to_show,
        padding,
        tracking_event_category,
        tracking_event_action,
        tracking_event_label,
        campaign_id,
        attributes = [],
        min_products_to_show,
    } = blockData || {};

    const selectedReminder = personalizedReminders?.active;
    const salesforceAttributes = useSelector(getSalesforceAttributes);
    const addUserAttributes = [];

    // Adjust slightly so that the fields defined in the CMS map to specific values, rather than filter - that way defaults can be sent when attributes differ between trending products
    addUserAttributes.push({ name: 'reminderOccasion', value: selectedReminder?.occasionCode || '' });
    addUserAttributes.push({ name: 'reminderReferenceProductId', value: selectedReminder?.product?.partNumber || '' });
    addUserAttributes.push({ name: 'reminderRecipientName', value: selectedReminder?.name || '' });
    addUserAttributes.push({ name: 'referenceProductCategory', value: salesforceAttributes?.categoryTabs?.referenceProductCategory || '' });
    addUserAttributes.push({ name: 'referenceProductId', value: salesforceAttributes?.categoryTabs?.referenceProductID || '' });

    const filterUserAttributes = (allAttributes, allowedAttributes) => allAttributes.filter((attr) => allowedAttributes.includes(attr?.name));

    const {
        loading, error, data, variables,
    } = useSalesforceProductRecommendationQuery({
        queryName: 'TrendingProducts',
        campaignFeature: campaign_id || 'Trending',
        targeting: {
            pageType: 'home',
        },
        optInHoldoutParticipation: true,
        resolveIdentity: true,
        filterByZipCode: true,
        addUserAttributes: filterUserAttributes(addUserAttributes, attributes),
    });

    const trackClickThrough = useClickStreamCustomEventsTracking({
        salesforceResponse: data?.productRecommendationsSFMulti?.[0],
        isFireImpression: true,
        page: { type: 'home' },
    });

    if (loading || variables?.skip) {
        if (campaign_id.includes('Pills')) {
            return (
                <TabTrendingProductSkeleton
                    nDisplayProducts={products_to_show}
                    breakPoints={{ mobile: 601, desktop: 769 }}
                />
            );
        }
        return (
            <CarouselBuilderSkeleton
                nDisplayProducts={products_to_show}
                breakPoints={{ mobile: 601, desktop: 769 }}
            />
        );
    }

    if (error) {
        mbpLogger.logError({
            function: 'TrendingProductsABContainer.js',
            message: 'Error loading data from saleforce api',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: error,
        });
    }

    const productClickCallback = (partNumber) => {
        trackClickThrough(partNumber);
        // call GA Events
        dispatch(trackEvent({
            eventCategory: tracking_event_category || 'Homepage',
            eventAction: tracking_event_action || 'Trending Gifts',
            eventLabel: tracking_event_label || 'Trending Gifts - Click',
            feature_category: tracking_event_category || 'Homepage',
            feature_element: tracking_event_action || 'Trending Gifts',
            feature_detail: tracking_event_label || 'Trending Gifts - Click',
        }));
    };

    if (min_products_to_show === 0) return null;

    if (data?.productRecommendationsSFMulti?.[0]?.products?.length >= min_products_to_show) {
        const title = data?.productRecommendationsSFMulti?.[0]?.campaign?.campaignResponses?.[0]?.payload?.productRecTitle;
        const carouselData = data?.productRecommendationsSFMulti?.[0]?.content?.personalized_carousel;
        const applyNewProductCardDesign = data?.productRecommendationsSFMulti?.[0]?.content?.personalized_product_card_reference?.length > 0;
        const productsToShow = {
            desktop: carouselData?.products_to_show?.desktop || products_to_show?.desktop,
            tablet: carouselData?.products_to_show?.tablet || products_to_show?.tablet,
            mobile: carouselData?.products_to_show?.mobile || products_to_show?.mobile,
        };
        const navPillsCampaignId = data?.productRecommendationsSFMulti?.[0]?.content?.navigation_pills_campaign_id;

        if (applyNewProductCardDesign) {
            return (
                <TrendingProductRecsBuilder data={data} cmsTitle={cmsTitle} products_to_show={productsToShow} recommendationsType="productRecommendationsSFMulti" productClickCallback={productClickCallback} />
            );
        }

        return (
            <ComponentErrorBoundary function="TrendingProductsABContainer" module="PersonalizedContent">
                <LoadableSimpleCarouselBuilder
                    blockData={{
                        productsToShow,
                        title: title || carouselData?.heading[0]?.copy || cmsTitle || 'Trending Products For You',
                        css_classes,
                        titleFontColor: carouselData?.heading[0]?.color?.color || title_font_color,
                        titleFontSize: carouselData?.heading[0]?.font_size || title_font_size,
                        titleFontStyle: carouselData?.heading[0]?.font_style || title_font_style,
                        titleFontFamily: carouselData?.heading[0]?.font_family || title_font_family,
                        titleFontWeight: carouselData?.heading[0]?.font_weight,
                        titleMargin: carouselData?.heading[0]?.margin || title_margin,
                        alignTitle: carouselData?.heading[0]?.align_title || align_title || '',
                        padding: carouselData?.padding || padding,
                    }}
                    products={data.productRecommendationsSFMulti?.[0]?.products || []}
                    productClickCallback={productClickCallback}
                    demandType="XS"
                    navPillsCampaignId={navPillsCampaignId}
                />
            </ComponentErrorBoundary>
        );
    }

    return null;
};

TrendingProductsABContainer.propTypes = {
    blockData: shape({
        title: string,
        title_font_color: shape({
            color: string.isRequired,
        }),
        title_font_size: string,
        title_font_style: string,
        title_font_family: string,
        align_title: string,
        padding: string,
        products_to_show: shape({
            desktop: number,
            tablet: number,
            mobile: number,
        }),
        campaign_id: string,
    }).isRequired,
};

export default TrendingProductsABContainer;
